/**
 * Design Tokens (JS)
 *
 * JS-side mirror of _task-tokens.scss for inline styles.
 * Import from here instead of hardcoding hex values in components.
 *
 * @see styles/tokens/_task-tokens.scss — canonical SCSS source
 */

// =============================================================================
// COLORS — Neutrals
// =============================================================================

export const neutral = {
  25: '#fafafa',
  50: '#f5f5f5',
  100: '#eeeeee',
  200: '#e0e0e0',
  300: '#bdbdbd',
  400: '#9e9e9e',
  500: '#757575',
  600: '#616161',
  700: '#424242',
  800: '#212121',
  900: '#121212',
};

// =============================================================================
// COLORS — Backgrounds
// =============================================================================

export const bg = {
  page: '#f8f9fa',
  card: '#ffffff',
  hover: '#fafafa',
  muted: neutral[100],
  subtle: neutral[50],
  warm: '#f9fafb',
};

// =============================================================================
// COLORS — Urgency
// =============================================================================

export const urgency = {
  critical: '#dc2626',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e',
};

// =============================================================================
// COLORS — Status (task workflow)
// =============================================================================

export const status = {
  notStarted: '#94a3b8',
  inProgress: '#3b82f6',
  review: '#f59e0b',
  completed: '#22c55e',
  canceled: '#ef4444',
  onHold: '#8b5cf6',
  blocked: '#dc2626',
  planning: '#8b5cf6',
};

// =============================================================================
// COLORS — Feedback (AntD-compatible)
// =============================================================================

export const feedback = {
  successBg: '#f6ffed',
  errorBg: '#fff2f0',
  errorBorder: '#ffccc7',
  errorText: '#cf1322',
  warningBg: '#fffbe6',
  warningBorder: '#ffe58f',
  warning: '#faad14',
  infoBg: '#e6f7ff',
  success: '#52c41a',
  error: '#ff4d4f',
  info: '#1890ff',
};

// =============================================================================
// COLORS — Links & Accents
// =============================================================================

export const link = {
  blue: '#3b82f6',
  blueHover: '#2563eb',
  blueBg: '#f0f9ff',
  blueText: '#1e40af',
};

export const accent = {
  primary: '#1890ff',
  emerald: '#10b981',
  sky: '#0284c7',
  sky800: '#075985',
  indigo: '#1e40af',
  amber: '#b45309',
};

// =============================================================================
// COLORS — Activity Type Icons (AntD palette for semantic indicators)
// =============================================================================

export const activityIcon = {
  success: '#52c41a',
  info: '#1890ff',
  purple: '#722ed1',
  orange: '#fa8c16',
  red: '#f5222d',
  magenta: '#eb2f96',
  gray: '#8c8c8c',
};

// =============================================================================
// COLORS — Text
// =============================================================================

export const text = {
  primary: '#212121',
  secondary: '#64748b',
  tertiary: '#94a3b8',
  muted: '#9ca3af',
  dark: '#1e293b',
  darkAlt: '#1f2937',
  body: '#374151',
  label: '#6b7280',
  heading: '#111827',
  placeholder: '#b0b0b0',
  hint: '#a8a8a8',
};

// =============================================================================
// COLORS — Borders
// =============================================================================

export const border = {
  default: '#e0e0e0',
  light: '#eeeeee',
  dark: '#bdbdbd',
  slate: '#e2e8f0',
  slateLight: '#f1f5f9',
  gray: '#e5e7eb',
  disabled: '#d9d9d9',
};

// =============================================================================
// SPACING (matches $space-* tokens — 4px base grid)
// =============================================================================

export const space = {
  0: 0,
  0.5: 2,
  1: 4,
  1.5: 6,
  2: 8,
  2.5: 10,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  7: 28,
  8: 32,
  9: 36,
  10: 40,
  11: 44,
  12: 48,
  14: 56,
  16: 64,
  20: 80,
};

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const fontSize = {
  caption: 12,
  meta: 13,
  button: 14,
  body: 15,
  subhead: 16,
  title: 18,
  heading: 22,
  display: 28,
};

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
};

// =============================================================================
// FONT FAMILY (mirrors $font-family-base / $font-family-mono in _task-tokens.scss)
// =============================================================================

export const fontFamily = {
  base: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mono: "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace",
};

// =============================================================================
// BORDER RADIUS
// =============================================================================

export const radius = {
  none: 0,
  xs: 4,
  sm: 6,
  md: 8,
  input: 10,
  lg: 12,
  xl: 16,
  '2xl': 20,
  '3xl': 24,
  full: 9999,
};

// =============================================================================
// ICON SIZING (glyph font-sizes)
// =============================================================================

export const icon = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
  xxl: 24,
};

// =============================================================================
// ICON BOX SIZING (click-target containers)
// =============================================================================

export const iconBox = {
  xs: 24,
  sm: 28,
  md: 32,
  lg: 36,
  xl: 40,
};

// =============================================================================
// DURATION (animation / transition timing)
// =============================================================================

export const duration = {
  micro: '0.08s',
  fast: '0.12s',
  base: '0.18s',
  slow: '0.28s',
  slower: '0.4s',
};

// =============================================================================
// SHADOWS (elevation system — mirrors $shadow-* SCSS tokens)
// =============================================================================

export const shadow = {
  xs: '0 1px 2px rgba(0,0,0,0.04)',
  sm: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  md: '0 4px 6px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.04)',
  lg: '0 10px 15px rgba(0,0,0,0.05), 0 4px 6px rgba(0,0,0,0.03)',
  xl: '0 20px 25px rgba(0,0,0,0.06), 0 8px 10px rgba(0,0,0,0.04)',
  card: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)',
  focus: '0 0 0 3px rgba(24,144,255,0.08), 0 0 0 1px #1890ff',
  primarySm: '0 1px 3px rgba(24,144,255,0.15)',
  primaryMd: '0 2px 6px rgba(24,144,255,0.15)',
  blueSm: '0 2px 8px rgba(59, 130, 246, 0.15)',
};

// =============================================================================
// OVERLAY BACKGROUNDS
// =============================================================================

export const overlay = {
  white: 'rgba(255, 255, 255, 0.98)',
  dark: 'rgba(0, 0, 0, 0.45)',
  light: 'rgba(255, 255, 255, 0.88)',
};

// =============================================================================
// HOVER BACKGROUNDS (semantic tinted overlays)
// =============================================================================

export const hoverBg = {
  blue: 'rgba(59, 130, 246, 0.08)',
  sky: 'rgba(14, 165, 233, 0.08)',
  primary: 'rgba(24, 144, 255, 0.1)',
};

// =============================================================================
// COMPONENT SIZES (height contract — mirrors $btn-height-*, $input-height-*)
// =============================================================================

export const size = {
  btnSm: 32,
  btnMd: 36,
  btnDefault: 40,
  btnLg: 44,
  iconBtnSm: 24,
  iconBtnMd: 28,
  iconBtnDefault: 32,
  iconBtnLg: 40,
  inputSm: 32,
  inputMd: 40,
  inputLg: 44,
  chipSm: 24,
  chipMd: 28,
  chipLg: 32,
  touchMin: 44,
  cardHeadHeight: 56,
  cardHeadPaddingY: 16,
};

// =============================================================================
// DATE FORMATS (governed moment/dayjs format strings)
// =============================================================================

// =============================================================================
// MODAL SIZES (governed widths for AntD Modal `width` prop)
// =============================================================================

export const modal = {
  sm: 400,    // Confirmations, simple alerts
  md: 560,    // Standard forms, multi-step dialogs
  lg: 720,    // Complex forms, detail views
  wide: 960,  // Help centers, documentation, read-heavy content with sidebar layouts
};

// =============================================================================
// DRAWER SIZES (governed widths for AntD Drawer `width` prop)
// =============================================================================

export const drawer = {
  md: 560,      // Medium — forms, simple lists
  lg: 750,      // Large — tables, detail views
  mobile: '100%', // Full-screen on mobile
};

// =============================================================================
// DATE FORMATS (governed moment/dayjs format strings)
// =============================================================================

export const dateFormat = {
  full: 'MMM DD, YYYY h:mm A',   // "Jan 05, 2025 2:30 PM" — timestamps, activity logs
  short: 'MMM DD h:mm A',        // "Jan 05 2:30 PM" — recent activity, compact timestamps
  dateOnly: 'MMM DD, YYYY',      // "Jan 05, 2025" — milestones, due dates, project dates
  compact: 'MM/DD',              // "01/05" — dense tables, sparkline labels, axis ticks
};

// =============================================================================
// PAGINATION (governed page size options)
// =============================================================================

export const pagination = {
  defaultPageSize: 10,
  pageSizes: [10, 25, 50, 100],
};
